/* core.scoring reads and writes.
 *
 * No `X-Lumiq-Tenant` header, here or anywhere in `src/`. Hard rule 1: the edge sets it from a
 * validated token claim. The vite proxy injects it in dev because the proxy stands in for
 * `api-gateway`, which is the only thing permitted to.
 *
 * Every type and guard below comes from `contracts/openapi/scoring.yaml` through
 * `tools/openapi_ts/generate.py`, and they are re-exported so screens and the presentation model
 * import from one place. The important thing the generator gives this feature specifically:
 * `Score.value` and `Coverage.column_ratio` are `number | null`, so `score.value < 70` does not
 * compile until the author has decided what to do about "no evidence".
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import {
  isEstateSummary,
  isEstateTrend,
  isScore,
  isScoreFactors,
  isScoreModel,
  isScorePage,
  isTrend,
} from '@/shared/api/generated/scoring';
import type {
  EstateSummary,
  EstateTrend,
  Score,
  ScoreFactors,
  ScoreModel,
  ScorePage,
  Trend,
} from '@/shared/api/generated/scoring';
import { getOne, idempotencyKeyFor, post } from '@/shared/api/http';

export type {
  CheckScope,
  ContextFactor,
  ContextKind,
  Contribution,
  Coverage,
  EstatePoint,
  EstateSummary,
  EstateTrend,
  Dimension,
  DimensionFactor,
  DimensionScore,
  DimensionState,
  FactorDirection,
  FactorImpact,
  RuleCheck,
  RuleFactor,
  Score,
  ScoreFactors,
  ScoreModel,
  ScorePage,
  ScoreSummary,
  Severity,
  Trend,
  TrendPoint,
} from '@/shared/api/generated/scoring';

const MODEL_KEY = ['scoring', 'model'] as const;
const SCORE_KEY = ['scoring', 'score'] as const;

export function useActiveModel(): UseQueryResult<ScoreModel> {
  return useQuery({
    queryKey: MODEL_KEY,
    queryFn: () => getOne('/v1/scoring/model', isScoreModel, 'score model'),
    retry: false,
  });
}

/* The estate's own score: one request, whatever the size of the estate.
 *
 * **This replaces a `BackendBadge`.** The front page's headline card carried a "needs an API" marker
 * for months, because the only way to build an estate mean from the rest of this API was one request
 * per dataset — the N+1 a dashboard is exactly where somebody writes. `GET /v1/scoring/estate`
 * aggregates server-side, so the payload is six numbers regardless.
 *
 * Not `staleTime: 0`: the figure moves when a scoring run completes, which is a scheduled sweep
 * rather than a user action, so re-fetching it on every mount of the front page buys nothing. The
 * default cache behaviour is right here and is stated so nobody "fixes" it.
 */
/** The span the front page's trend card names in its own heading. */
export const ESTATE_TREND_DAYS = 30;

export function useEstate(): UseQueryResult<EstateSummary> {
  return useQuery({
    queryKey: ['scoring', 'estate'],
    queryFn: () => getOne('/v1/scoring/estate', isEstateSummary, 'estate summary'),
  });
}

/* The estate's curve, for the front page's sparkline.
 *
 * Thirty days by default, which is what the card's own heading names. The span is a constant here
 * rather than a caller's argument because the heading and the request must not be able to disagree —
 * a card reading "30 days" over a 90-day line is the kind of wrongness nobody notices.
 */
export function useEstateTrend(
  days: number = ESTATE_TREND_DAYS,
): UseQueryResult<EstateTrend> {
  return useQuery({
    /* `days` is in the key, so switching windows can never render a 30-day series under a 90-day
     * label. Same reasoning as `useDatasetTrend`: a stale result under a new heading is worse than
     * a spinner. The default keeps the front page's caller — which names 30 days in its own
     * heading — reading exactly the series it names. */
    queryKey: ['scoring', 'estate', 'trend', days],
    queryFn: () =>
      getOne(
        `/v1/scoring/estate/trend?days=${String(days)}`,
        isEstateTrend,
        'estate trend',
      ),
  });
}

export function useDatasetScore(datasetId: string): UseQueryResult<Score> {
  return useQuery({
    queryKey: [...SCORE_KEY, datasetId],
    queryFn: () =>
      getOne(
        `/v1/scoring/datasets/${encodeURIComponent(datasetId)}/score`,
        isScore,
        'score',
      ),
    retry: false,
  });
}

/* One score by id, with its full decomposition. What run comparison is built out of.
 *
 * A `Trend` carries a `score_id` on every point but only the value and the coverage; the
 * dimensions, the contributions, the plan hash and the model version live on the `Score`. So
 * comparing two runs is exactly two reads of this — and **two, never N**. Fetching every point in a
 * window to draw per-dimension history would be the N+1 that `/scores/latest` exists to prevent,
 * one route over; a comparison is a pair by definition and stays affordable.
 *
 * Disabled without an id rather than called with an empty one, so a drawer that has not chosen a
 * second run yet reports "nothing selected" instead of a 404 it would then have to tell apart from
 * a real one.
 */
export function useScore(scoreId: string | undefined): UseQueryResult<Score> {
  return useQuery({
    queryKey: [...SCORE_KEY, 'by-id', scoreId],
    queryFn: () =>
      getOne(`/v1/scoring/scores/${encodeURIComponent(scoreId ?? '')}`, isScore, 'score'),
    enabled: scoreId !== undefined && scoreId.length > 0,
    /* A score is immutable once written — it records the run that produced it — so a refetch can
       only ever return the identical payload. Nothing here goes stale. */
    staleTime: Infinity,
    retry: false,
  });
}

/* What raised a score, what lowered it, and what it does not cover.
 *
 * Keyed under the score it explains and never stale, for `useScore`'s reason one step further: the
 * figures are the stored ones and the checks come from immutable rule versions, so a refetch can only
 * return the same payload. Disabled without an id, so the Score tab asks for an explanation only once
 * it has a score to explain.
 */
export function useScoreFactors(
  scoreId: string | undefined,
): UseQueryResult<ScoreFactors> {
  return useQuery({
    queryKey: [...SCORE_KEY, 'by-id', scoreId, 'factors'],
    queryFn: () =>
      getOne(
        `/v1/scoring/scores/${encodeURIComponent(scoreId ?? '')}/factors`,
        isScoreFactors,
        'score factors',
      ),
    enabled: scoreId !== undefined && scoreId.length > 0,
    staleTime: Infinity,
    retry: false,
  });
}

/** The trend. Empty rather than absent for an unscored dataset — see the contract.
 *
 * `days` is in the key, so switching windows cannot show a 30-day series under a 90-day label. That
 * is the same reasoning as the search term being in the rules list key: a stale result rendered under
 * a new heading is worse than a spinner.
 */
export function useDatasetTrend(datasetId: string, days: number): UseQueryResult<Trend> {
  return useQuery({
    queryKey: [...SCORE_KEY, datasetId, 'trend', days],
    queryFn: () =>
      getOne(
        `/v1/scoring/datasets/${encodeURIComponent(datasetId)}/trend?days=${String(days)}`,
        isTrend,
        'trend',
      ),
    /* Not fetched without a dataset to fetch for. The Scores screen reads the trend of whichever row
     * is selected, and there is no selection until the catalogue has loaded — an unguarded hook would
     * request `/datasets//trend` on the first render, which is a 404 the screen would then have to
     * distinguish from a real one. `enabled` rather than a conditional call, so the hook runs on every
     * render and React's rules of hooks hold. */
    enabled: datasetId.length > 0,
    retry: false,
  });
}

export interface ModelDraft {
  /** Dimension name → weight. Omitted dimensions are weighted **zero**, not inherited.
   *
   * An **empty** map is a different thing from a *partial* one and is sent as no map at all: the
   * server reads a present-but-empty `dimension_weights` as "every dimension explicitly zero",
   * which `ScoreModel` always refuses ("a model where everything is zero cannot produce a score").
   * Nobody ever wants that outcome, so an empty draft means "publish the platform defaults" and
   * must reach the server as an absent field, not as `{}`. */
  readonly weights: Readonly<Record<string, number>>;
  /** Severity name → weight, how much a binding's severity counts inside its dimension. Optional
   * and omitted when absent, so a draft that never touched severities publishes the server's own. */
  readonly severityWeights?: Readonly<Record<string, number>> | undefined;
  readonly notes: string | null;
}

export function usePublishModel(): UseMutationResult<ScoreModel, Error, ModelDraft> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (draft: ModelDraft) =>
      post(
        '/v1/scoring/model',
        /* Keyed on the weights themselves rather than on a counter or a timestamp. Publishing the
         * same weights twice is the retry case and should be one version; publishing different
         * weights is a new decision and gets a new key. */
        idempotencyKeyFor(
          'score-model',
          JSON.stringify([draft.weights, draft.severityWeights ?? null, draft.notes]),
        ),
        isScoreModel,
        'score model',
        {
          // Omitted, not `{}`, when the draft carries no weights — see `ModelDraft.weights`.
          ...(Object.keys(draft.weights).length > 0
            ? { dimension_weights: draft.weights }
            : {}),
          ...(draft.severityWeights === undefined
            ? {}
            : { severity_weights: draft.severityWeights }),
          notes: draft.notes,
        },
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: MODEL_KEY });
      /* Scores are computed under a model version, so a republish changes what a *future* score
       * means — but not any stored one. Only the model is invalidated; invalidating scores would
       * refetch identical numbers and imply they had moved. */
    },
    retry: false,
  });
}

export function useComputeScores(): UseMutationResult<ScorePage, Error, string> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (executionId: string) =>
      post(
        '/v1/scoring/scores',
        idempotencyKeyFor('compute-scores', executionId),
        isScorePage,
        'scores',
        { execution_id: executionId },
      ),
    /* **The whole `scoring` namespace, not `SCORE_KEY`, and that is a fix rather than a widening.**
     * React Query matches an invalidation by key *prefix*, and the estate listing caches under
     * `['scoring', 'latest', …]` (`catalog-browser/api/scores.ts`) while this invalidated
     * `['scoring', 'score']` — which does not match it. So a computed score landed in the database
     * and the Scores screen kept reading "Never scored" until something else happened to refetch:
     * the button appears to do nothing, which is worse than not having one.
     *
     * Every one of the four keys under `scoring` is derived from the rows this call writes — the
     * per-dataset score, the per-dataset trend, the estate figure on Home, and the estate listing —
     * so all four are genuinely stale afterwards. Invalidating the parent is narrower than it looks
     * and cannot miss one the way a hand-listed set already did. */
    onSettled: () => {
      void client.invalidateQueries({ queryKey: ['scoring'] });
    },
    retry: false,
  });
}
